import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams, Link } from 'react-router-dom';
import { subscriptionAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  Settings as SettingsIcon, 
  User,
  Building,
  CreditCard,
  Check,
  Loader2,
  Crown,
  Mail,
  Shield,
  Eye,
  EyeOff,
  Save,
  Key,
  Database,
  AlertTriangle,
  XCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

// Two-Factor Authentication Settings Component
const TwoFactorSettings = () => {
  const [twoFAStatus, setTwoFAStatus] = useState({ enabled: false, backup_codes_remaining: 0 });
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await api.get('/auth/mfa/status');
      setTwoFAStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch MFA status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async () => {
    try {
      const res = await api.post('/auth/mfa/setup');
      setSetupData(res.data);
    } catch (error) {
      toast.error('Failed to set up MFA');
    }
  };

  const handleVerify = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setVerifying(true);
    try {
      const res = await api.post('/auth/mfa/verify-setup', { code: verifyCode });
      toast.success('Two-factor authentication enabled!');
      if (res.data.backup_codes) {
        setBackupCodes(res.data.backup_codes);
      }
      setSetupData(null);
      fetchStatus();
    } catch (error) {
      toast.error('Invalid verification code');
    } finally {
      setVerifying(false);
    }
  };

  const handleDisable = async () => {
    const password = prompt('Enter your password to disable MFA:');
    if (!password) return;
    
    try {
      await api.post('/auth/mfa/disable', { password });
      toast.success('Two-factor authentication disabled');
      setBackupCodes([]);
      fetchStatus();
    } catch (error) {
      toast.error('Invalid password');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  // Show backup codes after successful setup
  if (backupCodes.length > 0) {
    return (
      <div className="space-y-6">
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <h4 className="font-medium text-amber-800 mb-2">Save your backup codes</h4>
          <p className="text-sm text-amber-700 mb-3">
            Store these codes safely. You can use them to access your account if you lose your authenticator.
          </p>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm">
            {backupCodes.map((code, i) => (
              <div key={i} className="bg-white p-2 rounded border">{code}</div>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                navigator.clipboard.writeText(backupCodes.join('\n'));
                toast.success('Backup codes copied!');
              }}
            >
              Copy Codes
            </Button>
            <Button 
              size="sm" 
              onClick={() => setBackupCodes([])}
            >
              Done - I&apos;ve Saved Them
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (setupData) {
    return (
      <div className="space-y-6">
        <div className="space-y-4">
          <div>
            <h4 className="font-medium mb-2">1. Scan QR Code</h4>
            <p className="text-sm text-slate-500 mb-3">
              Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </p>
            <div className="p-4 bg-white border rounded-lg inline-block">
              <img 
                src={setupData.qr_code}
                alt="MFA QR Code"
                className="w-48 h-48"
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Manual entry key: <code className="bg-slate-100 px-2 py-1 rounded">{setupData.manual_entry_key || setupData.secret}</code>
            </p>
          </div>
          
          <div>
            <h4 className="font-medium mb-2">2. Enter Verification Code</h4>
            <div className="flex gap-2 max-w-xs">
              <Input
                placeholder="000000"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                className="font-mono text-center text-lg tracking-widest"
                data-testid="mfa-verify-code-input"
              />
              <Button onClick={handleVerify} disabled={verifying} data-testid="mfa-verify-btn">
                {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Verify'}
              </Button>
            </div>
          </div>
        </div>
        
        <Button variant="outline" onClick={() => setSetupData(null)}>
          Cancel Setup
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="font-medium">Two-Factor Authentication</span>
          {twoFAStatus.enabled ? (
            <Badge className="bg-green-100 text-green-700">Enabled</Badge>
          ) : (
            <Badge variant="outline">Disabled</Badge>
          )}
        </div>
        <p className="text-sm text-slate-500">
          {twoFAStatus.enabled 
            ? `Your account is protected with MFA (${twoFAStatus.backup_codes_remaining || 0} backup codes remaining)`
            : 'Protect your account with an authenticator app'}
        </p>
      </div>
      {twoFAStatus.enabled ? (
        <Button variant="destructive" size="sm" onClick={handleDisable} data-testid="disable-mfa-btn">
          Disable MFA
        </Button>
      ) : (
        <Button onClick={handleSetup} data-testid="enable-mfa-btn">
          Enable MFA
        </Button>
      )}
    </div>
  );
};

export const Settings = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [tiers, setTiers] = useState({});
  const [currentTier, setCurrentTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [showSecrets, setShowSecrets] = useState({});
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [canceling, setCanceling] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const upgrade = searchParams.get('upgrade');
    
    if (sessionId && upgrade === 'success') {
      pollUpgradeStatus(sessionId);
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [tiersRes, currentRes] = await Promise.all([
        subscriptionAPI.getTiers(),
        subscriptionAPI.getCurrent()
      ]);
      setTiers(tiersRes.data?.tiers || tiersRes.data || {});
      setCurrentTier(currentRes.data);
      if (currentRes.data?.billing_cycle) {
        setBillingCycle(currentRes.data.billing_cycle);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollUpgradeStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) return;
    try {
      const response = await subscriptionAPI.checkUpgradeStatus(sessionId);
      if (response.data.payment_status === 'paid') {
        toast.success('Subscription upgraded successfully!');
        window.location.href = '/settings';
      } else {
        setTimeout(() => pollUpgradeStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Failed to check upgrade status:', error);
    }
  };

  const handleUpgrade = async (plan) => {
    setUpgrading(plan);
    try {
      const originUrl = window.location.origin;
      const response = await subscriptionAPI.upgrade(plan, billingCycle, originUrl);
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Failed to initiate upgrade:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate upgrade');
      setUpgrading(null);
    }
  };

  const handleCancelSubscription = async () => {
    setCanceling(true);
    try {
      const response = await subscriptionAPI.cancel(cancelReason);
      toast.success(response.data.message);
      setShowCancelDialog(false);
      setCancelReason('');
      // Refresh subscription data
      fetchData();
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setCanceling(false);
    }
  };

  const toggleShowSecret = (field) => {
    setShowSecrets(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Updated tier order for the new system
  const tierOrder = ['trial', 'professional', 'expert', 'enterprise'];
  
  // Calculate price based on billing cycle
  const getPrice = (tier) => {
    if (!tier) return 0;
    if (tier.is_custom_quote) return null;
    if (billingCycle === 'annual') {
      return tier.annual_price || (tier.monthly_price * 12 * (1 - (tier.annual_discount || 0) / 100));
    }
    return tier.monthly_price;
  };
  
  // Calculate monthly equivalent for annual plans
  const getMonthlyEquivalent = (tier) => {
    if (!tier || tier.is_custom_quote) return null;
    if (billingCycle === 'annual') {
      const annualPrice = tier.annual_price || (tier.monthly_price * 12 * (1 - (tier.annual_discount || 0) / 100));
      return annualPrice / 12;
    }
    return tier.monthly_price;
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your account, subscription, and integrations</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-2xl grid-cols-4">
          <TabsTrigger value="profile" className="gap-2">
            <User className="h-4 w-4" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-2">
            <Shield className="h-4 w-4" />
            Security
          </TabsTrigger>
          <TabsTrigger value="subscription" className="gap-2">
            <Crown className="h-4 w-4" />
            Subscription
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="space-y-6 mt-6">
          <Card data-testid="profile-section">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5" />
                Profile Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-blue-600 text-white text-xl">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="font-semibold text-slate-900">{user?.name}</h3>
                  <p className="text-sm text-slate-500">{user?.email}</p>
                  <Badge className="mt-2 capitalize">{user?.role}</Badge>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input value={user?.name || ''} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={user?.email || ''} disabled />
                </div>
              </div>
            </CardContent>
          </Card>

          {user?.firm_id && (
            <Card data-testid="firm-section">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building className="h-5 w-5" />
                  Firm Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label>Firm ID</Label>
                  <Input value={user.firm_id} disabled />
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-6 mt-6">
          <Card data-testid="security-section">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Two-Factor Authentication
              </CardTitle>
              <CardDescription>
                Add an extra layer of security to your account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <TwoFactorSettings />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Key className="h-5 w-5" />
                Password
              </CardTitle>
              <CardDescription>
                Change your password
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500 mb-4">
                Password changes are managed through your authentication provider.
              </p>
              <Button variant="outline" disabled>
                Change Password
              </Button>
            </CardContent>
          </Card>

          {/* Data Retention Link - for owners */}
          {user?.role === 'owner' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Data Retention Policies
                </CardTitle>
                <CardDescription>
                  Configure how long data is stored before automatic cleanup
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-4">
                  Manage retention policies for chat messages, notifications, and audit logs.
                </p>
                <Button asChild variant="outline">
                  <Link to="/settings/data-retention">
                    Manage Data Retention
                  </Link>
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Subscription Tab */}
        <TabsContent value="subscription" className="space-y-6 mt-6">
          <Card data-testid="subscription-section">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Crown className="h-5 w-5 text-yellow-500" />
                    Subscription Plans
                  </CardTitle>
                  <CardDescription>
                    Current plan: <span className="font-medium text-slate-900 capitalize">{currentTier?.plan_name || currentTier?.name || 'Trial'}</span>
                    {currentTier?.is_trial && currentTier?.trial_days_remaining !== undefined && (
                      <Badge className="ml-2 bg-amber-100 text-amber-700">
                        {currentTier.trial_days_remaining} days remaining
                      </Badge>
                    )}
                  </CardDescription>
                </div>
                
                {/* Billing Cycle Toggle */}
                <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg">
                  <button
                    onClick={() => setBillingCycle('monthly')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                      billingCycle === 'monthly' 
                        ? 'bg-white text-slate-900 shadow-sm' 
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setBillingCycle('annual')}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                      billingCycle === 'annual' 
                        ? 'bg-white text-slate-900 shadow-sm' 
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    Annual
                    <Badge className="ml-1.5 bg-green-100 text-green-700 text-xs">Save 15%</Badge>
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {tierOrder.map((tierKey) => {
                    const tier = tiers[tierKey];
                    if (!tier || tier.is_trial) return null;

                    const isCurrent = currentTier?.plan === tierKey;
                    const currentIndex = tierOrder.indexOf(currentTier?.plan || 'trial');
                    const tierIndex = tierOrder.indexOf(tierKey);
                    const isUpgrade = tierIndex > currentIndex;
                    const price = getPrice(tier);
                    const monthlyEquiv = getMonthlyEquivalent(tier);

                    return (
                      <Card 
                        key={tierKey} 
                        className={`relative ${isCurrent ? 'border-blue-500 border-2 shadow-lg' : ''} ${tierKey === 'expert' ? 'ring-2 ring-purple-200' : ''}`}
                        data-testid={`tier-${tierKey}`}
                      >
                        {isCurrent && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                            <Badge className="bg-blue-600">Current Plan</Badge>
                          </div>
                        )}
                        {tierKey === 'expert' && !isCurrent && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                            <Badge className="bg-purple-600">Most Popular</Badge>
                          </div>
                        )}
                        <CardContent className="p-5 pt-8">
                          <h3 className="font-semibold text-lg text-slate-900">{tier.name}</h3>
                          <p className="text-xs text-slate-500 mt-1 min-h-[32px]">{tier.description}</p>
                          
                          {tier.is_custom_quote ? (
                            <p className="text-2xl font-bold text-slate-900 mt-3">
                              Custom
                              <span className="text-sm font-normal text-slate-500 block">Contact sales</span>
                            </p>
                          ) : (
                            <div className="mt-3">
                              <p className="text-2xl font-bold text-slate-900">
                                {formatCurrency(monthlyEquiv)}
                                <span className="text-sm font-normal text-slate-500">/user/mo</span>
                              </p>
                              {billingCycle === 'annual' && (
                                <p className="text-xs text-green-600">
                                  {formatCurrency(price)}/user billed annually
                                </p>
                              )}
                            </div>
                          )}

                          <ul className="mt-4 space-y-1.5 text-sm">
                            {(Array.isArray(tier.features) ? tier.features.slice(0, 6) : []).map((feature) => (
                              <li key={feature} className="flex items-center gap-2 text-slate-600">
                                <Check className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                                <span className="capitalize text-xs">{feature.replace(/_/g, ' ')}</span>
                              </li>
                            ))}
                            {Array.isArray(tier.features) && tier.features.length > 6 && (
                              <li className="text-xs text-slate-400 pl-5">
                                +{tier.features.length - 6} more features
                              </li>
                            )}
                          </ul>

                          {tier.is_custom_quote ? (
                            <Button
                              className="w-full mt-4"
                              variant="outline"
                              onClick={() => window.open('mailto:sales@leappro.ca?subject=Enterprise%20Inquiry', '_blank')}
                            >
                              Contact Sales
                            </Button>
                          ) : (
                            <Button
                              className="w-full mt-4"
                              variant={isCurrent ? 'outline' : tierKey === 'expert' ? 'default' : 'secondary'}
                              disabled={isCurrent || !isUpgrade || upgrading === tierKey}
                              onClick={() => handleUpgrade(tierKey)}
                              data-testid={`upgrade-${tierKey}-btn`}
                            >
                              {upgrading === tierKey ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : isCurrent ? (
                                'Current Plan'
                              ) : isUpgrade ? (
                                'Upgrade'
                              ) : (
                                'Contact Support'
                              )}
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
              
              {/* Features Comparison Link */}
              <div className="mt-6 text-center">
                <p className="text-sm text-slate-500">
                  Need help choosing? <a href="#" className="text-blue-600 hover:underline">Compare all features</a>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Cancel Subscription Section */}
          {currentTier && (currentTier.status === 'active' || currentTier.status === 'trialing') && (
            <Card className="border-red-200 bg-red-50/50" data-testid="cancel-subscription-section">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                  <XCircle className="h-5 w-5" />
                  Cancel Subscription
                </CardTitle>
                <CardDescription className="text-red-600">
                  {currentTier.status === 'trialing' 
                    ? 'Cancel your trial - access will be revoked immediately.'
                    : currentTier.billing_cycle === 'annual'
                    ? 'Cancel your annual subscription - you will retain access until the end of your billing period. No prorated refund.'
                    : 'Cancel your subscription - you will retain access until the end of your current billing cycle.'
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="border-red-300 text-red-700 hover:bg-red-100"
                      data-testid="cancel-subscription-btn"
                    >
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      Cancel Subscription
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="flex items-center gap-2 text-red-700">
                        <AlertTriangle className="h-5 w-5" />
                        Cancel Your Subscription?
                      </AlertDialogTitle>
                      <AlertDialogDescription className="space-y-3">
                        <p>Are you sure you want to cancel your subscription?</p>
                        
                        {currentTier.status === 'trialing' ? (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
                            <strong>Trial Cancellation:</strong> Your trial will end immediately and you will lose access to all features.
                          </div>
                        ) : currentTier.billing_cycle === 'annual' ? (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
                            <strong>Annual Subscription:</strong> Your subscription will not renew. You will retain full access until {currentTier.subscription_period_end?.slice(0, 10) || 'the end of your billing period'}. No prorated refund will be issued.
                          </div>
                        ) : (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-amber-800">
                            <strong>Monthly Subscription:</strong> Your subscription will not renew. You will retain full access until {currentTier.subscription_period_end?.slice(0, 10) || 'the end of your billing cycle'}.
                          </div>
                        )}
                        
                        <div className="space-y-2">
                          <Label>Reason for canceling (optional)</Label>
                          <Textarea
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            placeholder="Help us improve by sharing why you're leaving..."
                            rows={3}
                          />
                        </div>
                        
                        <p className="text-sm text-slate-500">
                          Your data will be retained and available if you decide to resubscribe.
                        </p>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel disabled={canceling}>Keep Subscription</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleCancelSubscription}
                        disabled={canceling}
                        className="bg-red-600 hover:bg-red-700"
                        data-testid="confirm-cancel-btn"
                      >
                        {canceling ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Canceling...
                          </>
                        ) : (
                          'Yes, Cancel Subscription'
                        )}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardContent>
            </Card>
          )}

          {/* Canceled Subscription Notice */}
          {currentTier?.status === 'canceled' && (
            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Subscription Canceled</p>
                    <p className="text-sm text-amber-700">
                      Your subscription has been canceled. 
                      {currentTier.subscription_ends_at && (
                        <> You have access until {currentTier.subscription_ends_at.slice(0, 10)}.</>
                      )}
                    </p>
                    <Button 
                      className="mt-3" 
                      size="sm"
                      onClick={() => handleUpgrade(currentTier.plan || 'professional')}
                    >
                      Resubscribe
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
